if (!customElements.get('product-upsell-gift')) {
  class ProductUpsellGift extends HTMLElement {
    static _syncing = false;
    static _initialized = false;
    static STORAGE_KEY = 'product-upsell-gift-rules';
    static _knownFreeGiftVariantIds = new Set();

    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
    }

    static init() {
      if (ProductUpsellGift._initialized) return;
      ProductUpsellGift._initialized = true;

      const onCartChange = (event) => {
        ProductUpsellGift.scheduleApplyFreeGiftCartItemUI();
        ProductUpsellGift.syncAllButtonStates();

        if (event?.source === 'product-upsell-gift' || ProductUpsellGift._syncing) return;
        ProductUpsellGift.syncAllGifts();
      };

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, onCartChange);
      }

      const onReady = () => {
        ProductUpsellGift.hookCartDrawer();
        ProductUpsellGift.scheduleApplyFreeGiftCartItemUI();
        ProductUpsellGift.syncAllButtonStates();
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', onReady);
      } else {
        onReady();
      }
    }

    static hookCartDrawer() {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer || cartDrawer._upsellGiftHooked) return;
      if (typeof cartDrawer.renderContents !== 'function') return;

      cartDrawer._upsellGiftHooked = true;
      const originalRender = cartDrawer.renderContents.bind(cartDrawer);

      cartDrawer.renderContents = function (parsedState) {
        if (!parsedState?.sections) return;

        try {
          originalRender(parsedState);
        } catch (error) {
          console.error(error);
          return;
        }

        ProductUpsellGift.scheduleApplyFreeGiftCartItemUI();
      };
    }

    static updateCartDrawer(cartDrawer, data) {
      if (!cartDrawer || !data?.sections) return false;

      try {
        cartDrawer.classList.remove('is-empty');

        const drawerInner = cartDrawer.querySelector('.drawer__inner');
        if (drawerInner?.classList.contains('is-empty')) {
          drawerInner.classList.remove('is-empty');
        }

        cartDrawer.productId = data.id;

        cartDrawer.getSectionsToRender().forEach((section) => {
          const sectionElement = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);

          if (!sectionElement || !data.sections[section.id]) return;

          sectionElement.innerHTML = cartDrawer.getSectionInnerHTML(
            data.sections[section.id],
            section.selector
          );
        });

        ProductUpsellGift.scheduleApplyFreeGiftCartItemUI();
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    }

    static scheduleApplyFreeGiftCartItemUI() {
      requestAnimationFrame(() => {
        ProductUpsellGift.applyFreeGiftCartItemUI();
        setTimeout(() => ProductUpsellGift.applyFreeGiftCartItemUI(), 0);
        setTimeout(() => ProductUpsellGift.applyFreeGiftCartItemUI(), 150);
      });
    }

    static collectConfiguredGiftVariantIds() {
      const ids = new Set();
      const themeIds = (document.body?.dataset.upsellGiftVariantIds || '').split(',');

      themeIds.forEach((id) => {
        const parsed = parseInt(id.trim(), 10);
        if (parsed) ids.add(parsed);
      });

      ProductUpsellGift.getAllRules().forEach((rule) => {
        if (rule.giftVariantId) ids.add(rule.giftVariantId);
      });

      document.querySelectorAll('product-upsell-gift[data-gift-variant-id]').forEach((block) => {
        const parsed = parseInt(block.dataset.giftVariantId, 10);
        if (parsed) ids.add(parsed);
      });

      return ids;
    }

    static async getFreeGiftVariantIds() {
      const ids = new Set();

      ProductUpsellGift._knownFreeGiftVariantIds.forEach((id) => ids.add(id));

      try {
        const cart = await ProductUpsellGift.fetchCart();
        cart.items.forEach((item) => {
          const properties = item.properties || {};
          if (properties._upsell_free_gift || properties['_upsell_free_gift']) {
            ids.add(item.variant_id);
          }
        });
      } catch (error) {
        console.error(error);
      }

      return ids;
    }

    static getCartItemVariantId(row) {
      return parseInt(
        row.dataset.variantId ||
          row.querySelector('[data-quantity-variant-id]')?.dataset.quantityVariantId ||
          row.querySelector('[data-variant-id]')?.dataset.variantId,
        10
      );
    }

    static async applyFreeGiftCartItemUI() {
      const giftVariantIds = await ProductUpsellGift.getFreeGiftVariantIds();
      if (!giftVariantIds.size) return;

      document.querySelectorAll('.cart-item').forEach((row) => {
        const variantId = ProductUpsellGift.getCartItemVariantId(row);
        const isFreeGift = row.dataset.upsellFreeGift === 'true' || giftVariantIds.has(variantId);

        if (!isFreeGift) return;

        row.classList.add('cart-item--upsell-free-gift');
        row.dataset.upsellFreeGift = 'true';

        const customTitle = document.body?.dataset.upsellGiftCartTitle?.trim();
        const customSubtext = document.body?.dataset.upsellGiftCartSubtext?.trim();

        row.querySelectorAll('a.cart-item__name').forEach((link) => {
          const title = document.createElement('span');
          title.className = link.className;
          title.textContent = customTitle || link.textContent;
          link.replaceWith(title);
        });

        const detailsCell = row.querySelector('.cart-item__details');
        const nameEl = detailsCell?.querySelector('.cart-item__name');

        if (customTitle && nameEl) {
          nameEl.textContent = customTitle;
        }

        if (customSubtext && detailsCell && !detailsCell.querySelector('.cart-item__free-gift-subtext')) {
          const subtext = document.createElement('p');
          subtext.className = 'cart-item__free-gift-subtext';
          subtext.textContent = customSubtext;
          nameEl?.insertAdjacentElement('afterend', subtext);
        }

        row.querySelectorAll('a.cart-item__link').forEach((link) => link.remove());

        const qtyCell = row.querySelector('.cart-item__quantity');
        if (qtyCell) {
          qtyCell.innerHTML = '';
        }
      });
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);
      ProductUpsellGift.init();
      ProductUpsellGift.saveRule({
        pageProductId: this.getPageProductId(),
        upsellProductId: this.getUpsellProductId(),
        giftVariantId: this.getGiftVariantId(),
      });
      ProductUpsellGift.syncAllButtonStates();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    onClick(event) {
      const button = event.target.closest('[data-upsell-gift-add]');
      if (!button || button.disabled || button.classList.contains('is-added')) return;
      event.preventDefault();
      event.stopPropagation();
      this.addToCart(button);
    }

    static showAddedState(button) {
      button.classList.add('is-added');
      button.classList.remove('loading');
      button.disabled = true;
      button.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
    }

    static resetButtonState(button) {
      button.classList.remove('is-added', 'loading');
      button.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
      button.disabled = button.dataset.originallyDisabled === 'true';
    }

    static async syncAllButtonStates() {
      try {
        const cart = await ProductUpsellGift.fetchCart();

        document.querySelectorAll('[data-upsell-gift-add]').forEach((button) => {
          const variantId = parseInt(button.dataset.variantId, 10);
          if (!variantId) return;

          if (ProductUpsellGift.isVariantInCart(cart, variantId)) {
            ProductUpsellGift.showAddedState(button);
          } else {
            ProductUpsellGift.resetButtonState(button);
          }
        });
      } catch (error) {
        console.error(error);
      }
    }

    getPageProductId() {
      return parseInt(this.dataset.pageProductId, 10);
    }

    getUpsellProductId() {
      return parseInt(this.dataset.upsellProductId, 10);
    }

    getGiftVariantId() {
      return parseInt(this.dataset.giftVariantId, 10);
    }

    static getStoredRules() {
      try {
        return JSON.parse(sessionStorage.getItem(ProductUpsellGift.STORAGE_KEY) || '[]');
      } catch {
        return [];
      }
    }

    static saveRule(rule) {
      if (!rule.pageProductId || !rule.upsellProductId || !rule.giftVariantId) return;
      if (rule.pageProductId === rule.upsellProductId) return;

      const rules = ProductUpsellGift.getStoredRules();
      const exists = rules.some(
        (entry) =>
          entry.pageProductId === rule.pageProductId &&
          entry.upsellProductId === rule.upsellProductId &&
          entry.giftVariantId === rule.giftVariantId
      );

      if (!exists) {
        rules.push(rule);
        sessionStorage.setItem(ProductUpsellGift.STORAGE_KEY, JSON.stringify(rules));
      }
    }

    static getAllRules() {
      const rules = [...ProductUpsellGift.getStoredRules()];

      document.querySelectorAll('product-upsell-gift[data-gift-variant-id]').forEach((block) => {
        const rule = {
          pageProductId: parseInt(block.dataset.pageProductId, 10),
          upsellProductId: parseInt(block.dataset.upsellProductId, 10),
          giftVariantId: parseInt(block.dataset.giftVariantId, 10),
        };
        ProductUpsellGift.saveRule(rule);

        const exists = rules.some(
          (entry) =>
            entry.pageProductId === rule.pageProductId &&
            entry.upsellProductId === rule.upsellProductId &&
            entry.giftVariantId === rule.giftVariantId
        );

        if (!exists) rules.push(rule);
      });

      return rules;
    }

    static bothProductsInCart(cart, pageProductId, upsellProductId) {
      if (!pageProductId || !upsellProductId || pageProductId === upsellProductId) return false;

      return (
        ProductUpsellGift.isProductInCart(cart, pageProductId) &&
        ProductUpsellGift.isProductInCart(cart, upsellProductId)
      );
    }

    static async fetchCart() {
      const response = await fetch(`${routes.cart_url}.js`);
      return response.json();
    }

    static isProductInCart(cart, productId) {
      return cart.items.some((item) => item.product_id === productId);
    }

    static isVariantInCart(cart, variantId) {
      return cart.items.some((item) => item.variant_id === variantId);
    }

    static getVariantLineItem(cart, variantId) {
      return cart.items.find((item) => item.variant_id === variantId);
    }

    static getGiftRequirements(cart) {
      const requirements = new Map();
      const rules = ProductUpsellGift.getAllRules();

      rules.forEach((rule) => {
        const shouldHaveGift = ProductUpsellGift.bothProductsInCart(
          cart,
          rule.pageProductId,
          rule.upsellProductId
        );
        const current = requirements.get(rule.giftVariantId) || false;
        requirements.set(rule.giftVariantId, current || shouldHaveGift);
      });

      return requirements;
    }

    static async syncAllGifts() {
      if (ProductUpsellGift._syncing) return;

      const rules = ProductUpsellGift.getAllRules();
      if (!rules.length) return;

      try {
        ProductUpsellGift._syncing = true;

        const cart = await ProductUpsellGift.fetchCart();
        const requirements = ProductUpsellGift.getGiftRequirements(cart);

        for (const [giftVariantId, shouldHaveGift] of requirements) {
          const giftInCart = ProductUpsellGift.isVariantInCart(cart, giftVariantId);

          if (shouldHaveGift && !giftInCart) {
            await ProductUpsellGift.addGiftVariantToCart(giftVariantId);
            return;
          }

          if (!shouldHaveGift && giftInCart) {
            await ProductUpsellGift.removeGiftVariantFromCart(cart, giftVariantId);
            return;
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        ProductUpsellGift._syncing = false;
      }
    }

    static async addGiftVariantToCart(giftVariantId) {
      ProductUpsellGift._knownFreeGiftVariantIds.add(giftVariantId);

      const cartDrawer = document.querySelector('cart-drawer');
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData();
      formData.append('id', giftVariantId);
      formData.append('quantity', 1);
      formData.append('properties[_upsell_free_gift]', '1');

      if (cartDrawer) {
        formData.append(
          'sections',
          cartDrawer.getSectionsToRender().map((section) => section.id).join(',')
        );
        formData.append('sections_url', window.location.pathname);
      }

      config.body = formData;

      const response = await fetch(`${routes.cart_add_url}`, config);
      const data = await response.json();

      if (data.status) {
        throw new Error(data.description || data.message);
      }

      if (!ProductUpsellGift.updateCartDrawer(cartDrawer, data)) {
        ProductUpsellGift.scheduleApplyFreeGiftCartItemUI();
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-upsell-gift', cartData: data });
      }

      return data;
    }

    static async removeGiftVariantFromCart(cart, giftVariantId) {
      const giftLineItem = ProductUpsellGift.getVariantLineItem(cart, giftVariantId);
      if (!giftLineItem) return;

      const cartDrawer = document.querySelector('cart-drawer');
      const payload = {
        id: giftLineItem.key,
        quantity: 0,
      };

      if (cartDrawer) {
        payload.sections = cartDrawer.getSectionsToRender().map((section) => section.id).join(',');
        payload.sections_url = window.location.pathname;
      }

      const response = await fetch(`${routes.cart_change_url}`, {
        ...fetchConfig('javascript'),
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.status) {
        throw new Error(data.description || data.message);
      }

      if (!ProductUpsellGift.updateCartDrawer(cartDrawer, data)) {
        const cartPageItems = document.querySelector('cart-items');
        if (cartPageItems && typeof cartPageItems.onCartUpdate === 'function') {
          cartPageItems.onCartUpdate();
        }
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-upsell-gift', cartData: data });
      }

      return data;
    }

    async addToCart(button) {
      const variantId = button.dataset.variantId;
      if (!variantId) return;

      const cartDrawer = document.querySelector('cart-drawer');

      button.disabled = true;
      button.classList.add('loading');
      button.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');

      try {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', 1);

        if (cartDrawer) {
          formData.append(
            'sections',
            cartDrawer.getSectionsToRender().map((section) => section.id).join(',')
          );
          formData.append('sections_url', window.location.pathname);
        }

        config.body = formData;

        const response = await fetch(`${routes.cart_add_url}`, config);
        const data = await response.json();

        if (data.status) {
          throw new Error(data.description || data.message);
        }

        if (cartDrawer) {
          ProductUpsellGift.updateCartDrawer(cartDrawer, data);
        } else if (window.location.pathname.includes('/cart')) {
          window.location.reload();
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-upsell-gift', cartData: data });
        }

        ProductUpsellGift.showAddedState(button);
        setTimeout(() => ProductUpsellGift.syncAllGifts(), 0);
      } catch (error) {
        console.error(error);
        button.disabled = button.dataset.originallyDisabled === 'true';
        button.classList.remove('loading');
        button.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
      }
    }
  }

  customElements.define('product-upsell-gift', ProductUpsellGift);
  ProductUpsellGift.init();
}
